import React, { useCallback, useEffect, useState } from 'react';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactModal from 'shared/design-system/components/TrueImpactModal';
import styled from 'styled-components';
import {
  CardPeriodType,
  OnChangePeriod,
  YearKeyValuePair,
  getInitialYearValue,
  getPeriodText,
} from './utils';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { updateReportYear } from '../Review/api';
import { useClient } from 'shared/apollo';
import { ReportsForProgram } from '../../../ReportsBeta/NewReport/api';

interface YearModelProps {
  open: boolean;
  reportId: number;
  isThirdParty: boolean;
  report: ReportsForProgram | undefined;
  selectedPeriodType: CardPeriodType;
  startDate: Date | undefined;
  endDate: Date | undefined;
  reportYears: { [year: string]: boolean | undefined };
  periodType: CardPeriodType | undefined;
  reloadPrograms: () => unknown;
  onChangePeriod: OnChangePeriod;
  handleClose: () => unknown;
  setSelectedCard: (
    periodType?: CardPeriodType | undefined,
    start?: Date | undefined,
    end?: Date | undefined,
  ) => unknown;
}

const Tile: React.FC<{ year: number | string }> = ({ year }) => (
  <div className="modal-title">
    <TrueImpactText variant="heading-subheading">
      The reporting year set for this report is
    </TrueImpactText>
    <TrueImpactText variant="display-large">{year}</TrueImpactText>
  </div>
);

const StyledDiv = styled.div`
  padding: ${SPACERS.extraLarge};
  .main-text {
    max-width: 679px;
    text-align: center;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: ${SPACERS.medium};
  }
  .year-box {
    display: grid;
    gap: 10px;
    max-width: 342px;
    margin-left: auto;
    margin-right: auto;
    grid-template-columns: repeat(auto-fill, 50px);
    padding: ${SPACERS.medium};
    border-radius: ${SPACERS.tiny};
    border: 1px solid ${COLORS.grayLight};
    background-color: ${COLORS.whiteBase};
    .year {
      padding: ${SPACERS.tiny};
      border-radius: ${SPACERS.tiny};
      &:hover {
        color: ${COLORS.whiteBase};
        background-color: ${COLORS.ctaPrimary};
      }
      &.disabled:hover {
        background-color: unset;
        color: ${COLORS.grayBase};
      }
    }
    .year.selected {
      color: ${COLORS.whiteBase};
      background-color: ${COLORS.ctaPrimary};
    }
  }
`;

const getYears = (n: number): number[] => {
  const result: number[] = [];
  for (let i = n - 10; i <= n + 9; i++) {
    result.push(i);
  }
  return result;
};

const currentYear = new Date().getFullYear();
const years = getYears(currentYear);
const yearFormatter = (year: number): string => year.toString();

const YearModel: React.FC<YearModelProps> = ({
  open,
  reportId,
  isThirdParty,
  report,
  startDate,
  endDate,
  selectedPeriodType,
  periodType,
  reportYears,
  reloadPrograms,
  setSelectedCard,
  onChangePeriod,
  handleClose,
}) => {
  const client = useClient();
  const [requestUpdateYearState, makeUpdateYearRequest] = useGraphQLRequest();
  const [selectedYear, setSelectedYear] = useState<YearKeyValuePair>(
    getInitialYearValue(startDate, endDate, yearFormatter) || {
      text: currentYear.toString(),
      value: currentYear,
    },
  );

  useEffect(() => {
    if (requestUpdateYearState.type === 'REQUEST_SUCCESS') {
      handleClose();
      reloadPrograms();
    }
  }, [requestUpdateYearState.type]);

  const handleChangeYear = useCallback(
    (year: number) => {
      setSelectedYear({ text: year.toString(), value: year });
    },
    [setSelectedYear],
  );

  const handleUpdateYear = useCallback(() => {
    startDate?.setFullYear(selectedYear.value);
    endDate?.setFullYear(
      periodType === 'calendarYear'
        ? selectedYear.value
        : selectedYear.value + 1,
    );
    setSelectedCard(periodType, startDate, endDate);
    onChangePeriod({
      option: periodType || 'customYear',
      endDate,
      startDate,
    });
    makeUpdateYearRequest(
      updateReportYear(client!, reportId, selectedYear.value),
    );
  }, [startDate, endDate, periodType, selectedYear, setSelectedCard]);

  return (
    <TrueImpactModal
      open={open}
      showExpandButton={true}
      maxWidth="1000px"
      onClose={handleClose}
      errorText={
        requestUpdateYearState.type === 'REQUEST_ERROR'
          ? requestUpdateYearState.error.message
          : undefined
      }
      title={<Tile year={report?.year || selectedYear.value} />}
      previous={{
        onClick: handleClose,
        text: 'Cancel',
      }}
      next={{
        onClick: handleUpdateYear,
        text: 'Update year',
      }}
    >
      <StyledDiv>
        <TrueImpactText className="main-text">
          Because your reporting period is set as{' '}
          <strong>{getPeriodText(selectedPeriodType)}</strong>, we use the year
          during which the program begins as the year. You can update this
          below.
          <br />
          <br />
          <strong>Note:</strong> You cannot assign this report to a year that
          already has a published report.
          {isThirdParty && (
            <div>
              <strong>Note:</strong> For external reports, you can only select
              the past years.
            </div>
          )}
        </TrueImpactText>
        <div className="year-box">
          {years.map(y => {
            const disabled = isThirdParty
              ? y >= currentYear
              : reportYears[y.toString()];
            return (
              <TrueImpactText
                key={y}
                color={disabled ? 'grayBase' : undefined}
                onClick={disabled ? undefined : () => handleChangeYear(y)}
                className={`year${y === selectedYear.value ? ' selected' : ''}${
                  disabled ? ' disabled' : ''
                }`}
              >
                {y}
              </TrueImpactText>
            );
          })}
        </div>
      </StyledDiv>
    </TrueImpactModal>
  );
};

export default YearModel;
